/**
 * HR help text for the Travel module.
 *
 * A travel request is what later pays the employee. Once it is fully approved,
 * payroll reads its travel type and the number of nights and days against the
 * travel policy attached to the employee's request policy plan, so the travel
 * type and dates on this request decide the money.
 */

const travelRequest = {
  employment_id: "Employee who is travelling. Their approval workflow decides who receives the request, and their travel policy decides the amounts paid.",
  purpose: "Why the trip is needed. Approvers read this first.",
  description: "Any extra detail about the trip.",
  extension_no: "Office extension where the employee can be reached before travelling.",
  alternative_phone_no: "A number the company can reach the employee on while they are away.",
  is_advance_required: "Ask for money before travelling. An approved advance is deducted from the employee's salary in the payroll run, and the approved expense claim is paid back against it.",
  is_active: "Only active requests move through the approval workflow.",
  last_remarks: "Remarks written by the last approver. Visible in the request history.",
  reason: "Reason recorded with this request.",
  attachment: "Supporting document for the trip, such as an invitation or agenda.",
};

const travelDetail = {
  travel_type_id: "Type of travel, such as Local or International. Payroll pays the stay and daily allowance amounts set for this exact type in the travel policy, so a type with no row in the policy is paid nothing.",
  travel_mode_id: "How the employee travels, such as air, rail, or road.",
  from: "Place the employee is travelling from.",
  to: "Place the employee is travelling to.",
  departure_date: "Date the employee leaves. The daily allowance counts from this day.",
  departure_time: "Time the employee leaves.",
  arrival_date: "Date the employee returns. The daily allowance is paid for every day from departure to arrival, counting both.",
  arrival_time: "Time the employee returns.",
  distance: "Distance covered, where the allowance or claim depends on it.",
  is_stay_required: "Tick this when the employee needs overnight accommodation, which opens the stay details below.",
  date: "Date this leg of the travel takes place.",
  start_time: "Start time for this leg of the travel.",
  end_time: "End time for this leg of the travel.",
};

const hotelBooking = {
  nights: "Number of nights stayed. Payroll multiplies it by the per-night amount in the travel policy, so this number directly sets the stay allowance paid.",
  stay_type_id: "Whether the company arranged the stay or the employee arranged their own. The travel policy holds a different per-night amount for each.",
  book_from: "First night of the stay.",
  book_to: "Last night of the stay.",
  city: "City the employee is staying in.",
  address: "Address of the accommodation.",
  phone_no: "Contact number for the accommodation.",
  email: "Contact email for the accommodation.",
  name: "Name of the hotel or accommodation.",
};

const ticketBooking = {
  ticket_no: "Ticket or booking reference, kept so finance can match it to the invoice.",
  class: "Travel class booked, such as economy or business. It is checked against what the employee's grade allows.",
  from: "Departure point on the ticket.",
  to: "Destination on the ticket.",
  boarding_date: "Date shown on the ticket.",
  boarding_time: "Time shown on the ticket.",
  description: "Any note about this booking.",
};

const officialDuty = {
  employment_id: "Employee who was on official duty away from the office.",
  date: "The day spent on official duty. Once approved, that day is treated as duty rather than absent or late.",
  start_time: "Time the official duty started. Only the part of the shift outside this window is judged as normal attendance.",
  end_time: "Time the official duty ended.",
  reason: "What the employee was doing. Approvers read this before approving.",
  description: "Any extra detail about the duty.",
  last_remarks: "Remarks written by the last approver. Visible in the request history.",
};

const travelType = {
  name: "Name of this travel type, such as Local or International. The travel policy holds the stay and daily allowance amounts against this exact type, so employees are paid only for types the policy covers.",
  description: "Internal note explaining when this travel type should be chosen.",
  is_active: "Only active types can be selected on a new travel request. Existing requests keep the type they were raised with.",
};

const travelMode = {
  name: "Name of this travel mode, such as Air, Rail, or Road. It is offered on travel requests and can be limited per travel type in the travel policy.",
  description: "Internal note explaining when this mode should be chosen.",
  is_active: "Only active modes can be selected on a new travel request.",
};

const stayType = {
  name: "Name of this stay arrangement, such as Company Arranged or Self Arranged. The travel policy pays a different per-night amount for each, so the name should match how the policy is set up.",
  description: "Internal note explaining when this stay type should be chosen.",
  is_active: "Only active stay types can be selected on a new travel request.",
};

export const travelTooltips = { travelRequest, travelDetail, hotelBooking, ticketBooking, officialDuty, travelType, travelMode, stayType };
export default travelTooltips;
